import { Injectable } from '@angular/core';
import { Events } from 'ionic-angular';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import 'rxjs/add/operator/map';

import { LocalStorageService } from './local-storage';
import { EventNames } from '../lib/eventNames';
import { map } from 'rxjs/operators';

@Injectable()
export abstract class HttpAPIService {
	private authHeaderName: string = 'Authorization';
	private authTokenPrefix: string = 'Bearer ';
	private authTokenStorageEntity: string = 'authtokens';
    
    constructor(private http: HttpClient, private localStorage: LocalStorageService, private events: Events) { }

    getWithoutAuthAndContentType(url) {
        return new Promise((resolve, reject) => {
            var headers = new HttpHeaders();

            this.http.get(url, { headers: headers })
            .subscribe(data => resolve(data), error => reject(error))
        });
    }

    getWithoutAuth(url, transformJSON = true) {
        return new Promise((resolve, reject) => {
            var headers = new HttpHeaders();
            headers = headers.append('Content-Type', 'application/json');

            this.http.get(url, { headers: headers })
            .subscribe(data => resolve(data), error => reject(error))
        });
    }

    getObjectWithoutAuth<T>(url) : Promise<T> {
        return new Promise((resolve, reject) => {
            var headers = new HttpHeaders();
            headers = headers.append('Content-Type', 'application/json');

            this.http.get<T>(url, { headers: headers })
            .subscribe(data => resolve(data), error => reject(error))
        });
    }

    postWithoutAuth(url, data = undefined, transformJSON = true) {
        return new Promise((resolve, reject) => {
            var headers = new HttpHeaders();
            headers = headers.append('Content-Type', 'application/json');

            this.http.post(url, data, { headers: headers })
            .subscribe(data => resolve(data), err => reject(err))
        });
    }

    getFileContentsAuth<T>(url, body: any = undefined) : Promise<{data:T, contentType: string, filename: string}> {
        return new Promise((resolve, reject) => {
            this.localStorage.getEntity(this.authTokenStorageEntity)
            .then(tokens => {
                var headers = new HttpHeaders();
                headers = headers.append('Content-Type', 'application/json');
                headers = headers.append(this.authHeaderName, this.authTokenPrefix + tokens.accessToken);
                const options = { headers: headers, observe: 'response', responseType: 'blob' as 'json' } as any;

                (body ? this.http.post<T>(url, body, options) : this.http.get<T>(url, options))
                .pipe(
                    map((response: any) => {
                        const contentType = response.body.type;
                        const contentDisposition = response.headers.get('Content-Disposition');
                        const filename = this.extractFilename(contentDisposition);
                        return { data: response.body as T, contentType, filename };
                      })
                )
                .subscribe(data => resolve(data)
                , error => {
                    reject(error);

                    if (error.status == '401')
                        this.events.publish(EventNames.userLoginRequired);
                });
            });
        });
    }

    private extractFilename(contentDisposition: string | null): string {
        if (!contentDisposition) {
          return 'default-filename.csv'; //should probably use content-type to figure this out...
        }
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        return filenameMatch ? filenameMatch[1].replace(/['"]/g, '') : 'default-filename.csv';
      }

    getWithAuth<T>(url) : Promise<T> {
        return new Promise((resolve, reject) => {
            this.localStorage.getEntity(this.authTokenStorageEntity)
            .then(tokens => {
                var headers = new HttpHeaders();
                headers = headers.append('Content-Type', 'application/json');
                headers = headers.append('Platform', 'web');
                headers = headers.append(this.authHeaderName, this.authTokenPrefix + tokens.accessToken);

                this.http.get<T>(url, { headers: headers })
                .subscribe(data => resolve(data)
                , error => {
                    reject(error);

                    if (error.status == '401')
                        this.events.publish(EventNames.userLoginRequired);
                });
            });
        });
    }
    
    postWithAuthAndContentType(url, data = undefined, transformJSON = true) {
        return new Promise((resolve, reject) => {
            this.localStorage.getEntity(this.authTokenStorageEntity)
            .then(tokens => {
                var headers = new HttpHeaders();
                headers = headers.append(this.authHeaderName, this.authTokenPrefix + tokens.accessToken);
                headers = headers.append('AppVersion', '2');

                console.log("headers: ", headers);

                this.http.post(url, data, { headers: headers })
                .subscribe(data => resolve(data)
                , error => {
                    reject(error);

                    if (error.status == '401')
                        this.events.publish(EventNames.userLoginRequired);
                });
            });
        });
    }

    postWithAuth<T>(url, data = undefined, transformJSON = true) : Promise<T> {
        return new Promise((resolve, reject) => {
            this.localStorage.getEntity(this.authTokenStorageEntity)
            .then(tokens => {
                var headers = new HttpHeaders();
                headers = headers.append('Content-Type', 'application/json');
                headers = headers.append(this.authHeaderName, this.authTokenPrefix + tokens.accessToken);
                headers = headers.append('AppVersion', '2');

                this.http.post<T>(url, data, { headers: headers })
                .subscribe(data => resolve(data)
                , error => {
                    reject(error);

                    if (error.status == '401')
                        this.events.publish(EventNames.userLoginRequired);
                });
            });
        });
    }

    patchWithAuth(url, data = undefined) {
        return new Promise((resolve, reject) => {
            this.localStorage.getEntity(this.authTokenStorageEntity)
            .then(tokens => {
                var headers = new HttpHeaders();
                headers = headers.append('Content-Type', 'application/json');
                headers = headers.append(this.authHeaderName, this.authTokenPrefix + tokens.accessToken);

                this.http.patch(url, data, { headers: headers })
                .subscribe(data => resolve(data)
                , error => {
                    reject(error);

                    if (error.status == '401')
                        this.events.publish(EventNames.userLoginRequired);
                });
            });
        });
    }

    putWithAuth(url, data = undefined, transformJSON = true) {
        return new Promise((resolve, reject) => {
            this.localStorage.getEntity(this.authTokenStorageEntity)
            .then(tokens => {
                var headers = new HttpHeaders();
                headers = headers.append('Content-Type', 'application/json');
                headers = headers.append(this.authHeaderName, this.authTokenPrefix + tokens.accessToken);

                this.http.put(url, data, { headers: headers })
                .subscribe(data => resolve(data)
                , error => {
                    reject(error);

                    if (error.status == '401')
                        this.events.publish(EventNames.userLoginRequired);
                });
            });
        });
    }

    putWithoutAuth(url, data = undefined) {
        return new Promise((resolve, reject) => {
            this.localStorage.getEntity(this.authTokenStorageEntity)
            .then(tokens => {
                var headers = new HttpHeaders();
                headers = headers.append('Content-Type', 'application/json');

                this.http.put(url, data, { headers: headers })
                .subscribe(data => resolve(data), error => reject(error));
            });
        });
    }
    
    deleteWithAuth(url) {
        return new Promise((resolve, reject) => {
            this.localStorage.getEntity(this.authTokenStorageEntity)
            .then(tokens => {
                var headers = new HttpHeaders();
                headers = headers.append('Content-Type', 'application/json');
                headers = headers.append(this.authHeaderName, this.authTokenPrefix + tokens.accessToken);

                this.http.delete(url, { headers: headers })
                .subscribe(data => resolve(data)
                , error => {
                    reject(error);

                    if (error.status == '401')
                        this.events.publish(EventNames.userLoginRequired);
                });
            });
        });
    }
}


// WEBPACK FOOTER //
// ./src/services/httpAPIservice.ts